import { useDpr } from '@hooks/useDpr';
import { useImage } from '@hooks/useImage';
import { twMerge } from '@utils/tailwind';
import _ from 'lodash';
import React, { ForwardedRef, forwardRef, PointerEvent, RefObject, useEffect, useImperativeHandle, useRef, useState } from 'react';

export type ImageAnnotationCanvasRefType = {
  canvasRef: RefObject<HTMLCanvasElement | null>
  dirty: boolean
  reset: () => void
}

type Props = {
  imageUrl?: string
  className?: string
}

function $ImageAnnotationCanvas({ imageUrl, className }: Props, ref: ForwardedRef<ImageAnnotationCanvasRefType>) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dpr = useDpr()
  const [isDrawing, setIsDrawing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [imageRef, imageLoaded, imageSize] = useImage(imageUrl)

  const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const ctx = canvasRef.current?.getContext('2d');
    const clientRect = canvasRef.current?.getBoundingClientRect();
    if (ctx && clientRect) {
      const dpr = (window.devicePixelRatio || 1)
      ctx.beginPath();
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#ff0000';
      const relativePosition = [
        (event.clientX - clientRect.left) / clientRect.width,
        (event.clientY - clientRect.top) / clientRect.height,
      ]
      const actualPosition = [relativePosition[0] * ctx.canvas.width / dpr, relativePosition[1] * ctx.canvas.height / dpr]
      ctx.moveTo(actualPosition[0], actualPosition[1]);
    }
  }

  const handlePointerUp = () => {
    setIsDrawing(false);
    const ctx = canvasRef.current?.getContext('2d')
    if (ctx) {
      ctx.closePath();
    }
  }

  const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvasRef.current?.getContext('2d');
    const clientRect = canvasRef.current?.getBoundingClientRect();
    if (isDrawing && clientRect && ctx) {
      if (!isDirty) setIsDirty(true);
      const dpr = (window.devicePixelRatio || 1)
      const relativePosition = [
        (event.clientX - clientRect.left) / clientRect.width,
        (event.clientY - clientRect.top) / clientRect.height,
      ]
      const actualPosition = [relativePosition[0] * ctx.canvas.width / dpr, relativePosition[1] * ctx.canvas.height / dpr]
      ctx.lineTo(actualPosition[0], actualPosition[1]);
      ctx.stroke();
    }
  }

  useImperativeHandle(ref, () => ({
    canvasRef,
    dirty: isDirty,
    reset: () => {
      setIsDirty(false);
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx && imageRef.current) {
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.drawImage(imageRef.current, 0, 0);
      }
    }
  }), [canvasRef, imageRef, isDirty]);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx && canvasRef.current && imageRef.current) {
      canvasRef.current.width = imageRef.current.width * dpr;
      canvasRef.current.height = imageRef.current.height * dpr;

      ctx.save();
      ctx.moveTo(0, 0)
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(imageRef.current, 0, 0)

      return () => {
        ctx.restore()
      }
    }

    return () => {}
  }, [dpr, imageLoaded, imageSize]);

  useEffect(() => {
    if (imageRef.current && imageUrl) {
      imageRef.current.src = imageUrl;
      imageRef.current.setAttribute('crossOrigin', 'anonymous');
    }
  }, [imageUrl]);

  return (
    <canvas
      ref={canvasRef}
      className={twMerge('touch-none', className)}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerMove={handlePointerMove}
    />
  )
}

export const ImageAnnotationCanvas = forwardRef($ImageAnnotationCanvas);
